/**
 * Profile Service Module
 * Handles user profile management including creation, retrieval,
 * updates, and profile photo uploads.
 */

import Parse from "./parse.service";
import { auth } from "services";

const Profile = Parse.Object.extend("Profile");
const user = auth.fetchUser();

/**
 * Retrieves the current user's profile, creates one if it doesn't exist
 * @returns {Promise<Parse.Object>} The user's profile object
 * @throws {Parse.Error} If profile retrieval fails
 */
export const getProfile = async () => {
    const query = new Parse.Query(Profile);
    query.equalTo("userId", user);
    try {
        const results = await query.find();        
        if(results.length==0){
          let profile = new Profile()
            profile.set("userId", user);
            await profile.save();
            return getProfile();
        }
        
        return results[0];
    } catch (err) {
        return err;
    }
}

/**
 * Updates the user's profile information
 * @param {Object} profileData - Object containing profile data to update
 * @param {string} [profileData.phone] - User's phone number
 * @param {string} [profileData.businessName] - Name of the user's business
 * @param {string} [profileData.typeOfBusiness] - Type/category of the business
 * @param {string} [profileData.photo] - URL of the profile photo
 * @returns {Promise<Parse.Object>} Updated profile object
 */
export const updateProfile = async (profileData) => {
    try {
        const profile = await getProfile();
        profile.set("userId", user);
        profileData.phone? profile.set("phone", profileData.phone) : null;
        //profileData.email? profile.set("email", profileData.email) : null;
        profileData.businessName? profile.set("businessName", profileData.businessName) : null;
        profileData.typeOfBusiness? profile.set("typeOfBusiness", profileData.typeOfBusiness) : null;
        profileData.photo? profile.set("profilePhoto", profileData.profilePhoto) : null;
        return profile.save();
    } catch(error){
        console.error("Error updating profile:", error);
    }
}

/**
 * Updates the user's onboarding status
 * @param {boolean} status - New onboarding status
 * @returns {Promise<Parse.User>} Updated user object
 */
export function setOnboardingInfo(status) {
    user.set('onboardingInfo', status)
    return user.save();
}

/**
 * Sets the type of business for the user
 * @param {string} typeOfBusiness - Type of business to set
 * @returns {Promise<Parse.User>} Updated user object
 */
export function setTypeOfBusiness(typeOfBusiness) {
    user.set('typeOfBusiness', typeOfBusiness)
    return user.save();
}

/**
 * Uploads an image file with progress tracking and timeout
 * @param {File} file - The image file to upload
 * @param {Function} setProgress - Callback function to update upload progress
 * @returns {Promise<Parse.File>} Uploaded file object
 * @throws {Error} If upload fails or times out after 25 seconds
 */
export async function uploadImage(file,setProgress) {
    const currentTime = Date.now().toString();
    const name = "Images"+currentTime;

    // Create a timeout promise that rejects after 25 seconds
    const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout: Upload took too long')), 25000)
    );
    try {
        // Use Promise.race to execute both the upload and timeout concurrently
        const result = await Promise.race([
            (async () => {
                var parseFile = new Parse.File(name, file);
                return await parseFile.save({
                    progress: (progressValue, loaded, total, { type }) => {
                        if (type === "upload" && progressValue !== null) {
                            setProgress(progressValue*100);           
                        }
                    },
                    error: (error) => {
                        throw new Error("file upload failed", error);
                    },
                    
                });
            })(),
            timeoutPromise
        ]);

        return result;
    } catch (error) {
        console.log("Error uploading file:", error);
    }
}


